<template>
  <section class="portfolio" id="portfolio">
    <div class="container">
      <section-title data-aos="fade-up" :kicker="kicker" :title="title" :lede="lede" />

      <div class="row">
        <div
          v-for="project in projects"
          :key="project.slug"
          class="col-md-6 col-lg-4"
          data-aos="fade-up"
        >
          <portfolio-card :project="project" />
        </div>
      </div>

      <!-- The listing is the whole archive now, so the band is the work worth
           showing first and this is how the reader gets to the rest. The button
           follows the work rather than preceding it: a "view everything" action
           above the grid asks the reader to skip what they came to see. -->
      <div class="action-row" data-aos="fade-up">
        <app-button :to="route('portfolios')" icon="arrow-right" icon-after>
          {{ t('View all projects') }}
        </app-button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

.portfolio {
  /* The band, on the home page and on the web design page. The cards, the
     screenshots, their overlay and the "New" tab are all PortfolioCard's now;
     this section owns only where they sit.

     The id is the anchor the header used to scroll to before the portfolio had a
     page of its own. It stays because that URL has been published and linked, and
     a stored `/#portfolio` should still land on the work rather than on the top
     of the home page. */

  /* Centred, because the section head above it is centred. The shared row is
     left-aligned, which is what a page that puts it under a left-hand column
     wants; a centred block overrides it, the way the error page's own centred
     block already does. */
  .action-row {
    justify-content: center;
  }
}
</style>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import PortfolioCard from '@/Components/PortfolioCard.vue';
import SectionTitle from '@/Components/SectionTitle.vue';

export default {
  components: {
    AppButton,
    PortfolioCard,
    SectionTitle
  },

  props: {
    /* The featured projects, passed in by the two pages that show this band.
       They come from `PortfolioCatalog::featured()`, so the band and the
       listing cannot disagree about what a card says, and which projects are in
       the band is decided in one place rather than listed again here. */
    projects: {
      type: Array,
      default: () => []
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          "Despite being a fresh startup, we've experiences working with big brands and corporates. Check out our previous project, or simply take a look of our own website. You can certainly feel the differences.": "Despite being a fresh startup, we've experiences working with big brands and corporates. Check out our previous project, or simply take a look of our own website. You can certainly feel the differences.",
          "Enough Talking, Let's See What Matters The Most.": "Enough Talking, Let's See What Matters The Most.",
          "What We've Done So Far": "What We've Done So Far",
          'View all projects': 'View all projects'
        },
        'zh-MY': {
          "Despite being a fresh startup, we've experiences working with big brands and corporates. Check out our previous project, or simply take a look of our own website. You can certainly feel the differences.": '尽管我们是一家新兴公司，但我们与各大品牌的公司合作经验丰富。您可以查看曾经与我们合作过的公司以及其项目，又或者是浏览看看我们本身的网站。我们相信眼尖的您自然可以发现其中的差异。',
          "Enough Talking, Let's See What Matters The Most.": '话不多说，让我们看看什么才是最重要的。',
          "What We've Done So Far": '到目前为止我们所做的',
          'View all projects': '查看全部项目'
        },
        'ms-MY': {
          "Despite being a fresh startup, we've experiences working with big brands and corporates. Check out our previous project, or simply take a look of our own website. You can certainly feel the differences.": 'Walaupun kami sesuatu syarikat permulaan yang baru, kami telah bekerjasama dengan syarikat-syarikat besar. Mari lihat projek kami setakat ini, atau cuba lihat laman web kami sendiri. Kami pasti anda akan mendapati perbezaan kami.',
          "Enough Talking, Let's See What Matters The Most.": 'Tanpa basa-basi lagi, mari kita lihat apa yang paling penting.',
          "What We've Done So Far": 'Projek Kami Setakat Ini',
          'View all projects': 'Lihat semua projek'
        }
      }
    });

    /* These three sentences each contain an apostrophe, which cannot be quoted
       inside a Vue attribute, so they are computed rather than inline. A
       computed keeps them reactive to a locale change. */
    const kicker = computed(() => t("Enough Talking, Let's See What Matters The Most."));
    const title = computed(() => t("What We've Done So Far"));
    const lede = computed(() => t("Despite being a fresh startup, we've experiences working with big brands and corporates. Check out our previous project, or simply take a look of our own website. You can certainly feel the differences."));

    return { t, kicker, lede, title };
  }
};
</script>
